import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Tooltip,
  ListItemSecondaryAction,
  Collapse,
} from '@mui/material';
import {
  StarBorder as FavoritesIcon,
  Home as HomeIcon,
  FolderCopy as ProjectsIcon,
  People as UsersIcon,
  QuestionAnswer as GroupsIcon,
  Settings as SettingsIcon,
  Business as ClientsIcon,
  Security as RolesIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  canAccessSurveyManage,
  canAccessSurveyModule,
  canAccessSurveyResponses,
  SURVEY_MANAGE_ROUTE_PERMISSIONS,
} from '../../constants/surveyAccessPermissions';
import { isSurveyOnlyUser } from '../../constants/permissions';
import { LAYOUT } from '../../utils/layoutConstants';
import './Sidebar.css';

const Sidebar = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTenant, availableTenants, switchTenant, isJLLUser, hasRole, hasPermission, userPermissions } = useTenant();
  const [selectedTenant, setSelectedTenant] = useState('');
  useEffect(() => {
    if (currentTenant) {
      setSelectedTenant(currentTenant.tenantId || '');
    }
  }, [currentTenant]);

  const handleTenantChange = async (event) => {
    const tenantId = event.target.value;
    setSelectedTenant(tenantId);
    
    if (switchTenant) {
      await switchTenant(tenantId);
      // Navigate to home page to avoid errors when the current route 
      // is not accessible in the new tenant. 
      // HomeEntry handles specific role redirections like Client Viewer.
      navigate('/');
    }
  };

  const tenantsList = Array.isArray(availableTenants) ? availableTenants : [];

  const canSeeSurvey = canAccessSurveyModule(hasPermission);
  const canSeeSurveyManage = canAccessSurveyManage(hasPermission);
  const canSeeSurveyResponses = canAccessSurveyResponses(hasPermission);
  const surveyOnly = isSurveyOnlyUser(userPermissions);

  const navigationItems = surveyOnly
    ? []
    : [
        { label: 'Favorites', icon: <FavoritesIcon />, path: '/favorites' },
        { label: 'Home', icon: <HomeIcon />, path: '/' },
      ];

  const managementItems = [
    { label: 'Workspace', icon: <ProjectsIcon />, path: '/projects', permission: 'create_dashboard' },
    { label: 'Users', icon: <UsersIcon />, path: '/users', permission: 'manage_users' },
    { label: 'Clients', icon: <ClientsIcon />, path: '/clients', requireJLLSuperAdmin: true },
    { label: 'Roles', icon: <RolesIcon />, path: '/roles', requireJLLSuperAdmin: true },
    // Question groups: anyone who may create a survey may use/curate templates.
    {
      label: ' Question Groups',
      icon: <GroupsIcon />,
      path: '/groups',
      anyPermission: SURVEY_MANAGE_ROUTE_PERMISSIONS,
    },
    { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    const active = isActive(item.path);
    
    const handleNavigation = (e) => {
      if (window.__checkNavigation && !window.__checkNavigation(item.path)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      navigate(item.path);
    };
    
    return (
      <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={handleNavigation}
          sx={{
            minHeight: '3rem',
            justifyContent: open ? 'initial' : 'center',
            px: 2.5,
            backgroundColor: active ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
            '&:hover': {
              backgroundColor: active ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
            },
            borderLeft: active ? '3px solid #000000' : '3px solid transparent',
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
              color: active ? '#000000' : '#666666',
            }}
          >
            {item.icon}
          </ListItemIcon>
          {open && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: active ? 600 : 400,
                color: active ? '#000000' : '#333333',
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerWidth = open ? LAYOUT.SIDEBAR_WIDTH : LAYOUT.SIDEBAR_WIDTH_COLLAPSED;

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#F5F5F5',
          borderRight: '1px solid #E0E0E0',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          top: `${LAYOUT.HEADER_HEIGHT}px`,
          height: `calc(100vh - ${LAYOUT.HEADER_HEIGHT}px - ${LAYOUT.FOOTER_HEIGHT}px)`,
          bottom: `${LAYOUT.FOOTER_HEIGHT}px`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          pt: 2,
        }}
      >
        {open && (
          <Box sx={{ px: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <Select
                value={selectedTenant}
                onChange={handleTenantChange}
                displayEmpty
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '0.5rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#000000',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#000000',
                  },
                }}
                renderValue={(value) => {
                  const tenant = tenantsList.find((t) => t.tenantId === value);
                  return tenant ? tenant.tenantName : currentTenant?.tenantName || 'Select Tenant';
                }}
              >
                {tenantsList.length > 0 ? (
                  tenantsList.map((tenant) => (
                    <MenuItem 
                      key={tenant.tenantId} 
                      value={tenant.tenantId}
                      sx={{
                        backgroundColor: selectedTenant === tenant.tenantId ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: selectedTenant === tenant.tenantId ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                        <Typography>{tenant.tenantName}</Typography>
                        {selectedTenant === tenant.tenantId && (
                          <CheckIcon sx={{ ml: 1, color: '#000000', fontSize: '1.2rem' }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No tenants available</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: open ? 'flex-end' : 'center',
            px: 1,
            mb: 1,
          }}
        >
          
            <IconButton
              onClick={onToggle}
              sx={{
                backgroundColor: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                },
              }}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
       
        </Box>

        <List sx={{ flexGrow: 1, px: 1 }}>
          {navigationItems.map((item) => (
            <Tooltip key={item.path} title={!open ? item.label : ''} placement="right">
              {renderNavItem(item)}
            </Tooltip>
          ))}
        </List>

        <Divider sx={{ mx: 2, my: 1 }} />

        <List sx={{ px: 1, pb: 2 }}>
          {!surveyOnly && managementItems
            .filter((item) => {
              if (item.requireJLLSuperAdmin) {
                return isJLLUser() && hasRole('JLL Super Admin');
              }
              if (item.anyPermission) {
                return item.anyPermission.some((code) => hasPermission(code));
              }
              if (item.permission) {
                return hasPermission(item.permission);
              }
              return true;
            })
            .map((item) => (
              <Tooltip key={item.path} title={!open ? item.label : ''} placement="right">
                {renderNavItem(item)}
              </Tooltip>
            ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
